/* eslint-disable @typescript-eslint/no-explicit-any */
// API Request/Response types based on the Django backend contract

// Authentication types
export interface AuthRequest {
  username?: string;
  password?: string;
  refresh_token?: string;
  grant_type?: "password" | "refresh_token";
  role: "manager" | "verificator" | "operator";
}

export interface AuthResponse {
  success: boolean;
  token?: string;
  refresh_token?: string;
  user?: {
    id: string;
    username: string;
    role: string;
  };
  message?: string;
  // Add actual API error response format
  error_message?: string;
  code?: string;
  validation_error?: any;
}

// Member management types
export interface MemberRequest {
  action: "create" | "update" | "delete" | "list";
  params?: {
    role: "verificator" | "operator";
  };
  slug?: string;
  data?: object;
}

export type MemberResponse = VerificatorMember[] | VerificatorMember;

export interface VerificatorMember {
  id: string;
  full_name: string;
  created_at: string;
  username: string;
  isActive: boolean;
}

// Shift management types
export interface Shift {
  id: string;
  name: string;
  status: "active" | "done" | "locked";
  start_time: string;
  end_time: string;
  pic_name: string;
  device_id: string;
  created_at: string;
}

export interface CreateShiftRequest {
  device_id: string;
  cell_type?: string;
}

export interface ShiftStatusRequest {
  new_status: "active" | "done" | "locked";
}

// Barcode scan history types
export interface ApiScanRecord {
  id?: string;
  barcode: string;
  created_at: string;
  scanned_at: string;
  device_id: string;
  operator_full_name: string;
  operator_username: string;
  is_manual_override: boolean;
  shift_id: string;
  shift_name: string;
}

export interface BulkScanRequest {
  scans: Omit<ApiScanRecord, "id">[];
}

export interface ScanHistoryResponse {
  data: ApiScanRecord[];
  next_page?: string;
  previous_page?: string;
  count_items: number;
}

export interface ScannedBarcode {
  id: string;
  barcode: string;
  scanned_at: string;
  operator_username: string;
  operator_full_name: string;
  shift_id: string;
  shift_name: string;
  device_id: string;
  created_at: string;
}

// Bitbox block types
export interface BitboxBlock {
  id: string;
  status: "pass" | "fail";
  shift_id: string;
  scanned_barcode: ScannedBarcode[];
  created_at: string;
}

export interface BitboxBlockRequest {
  barcode_data: {
    code: string;
    slotId: string;
    timestamp: string;
    sessionId: string;
    isManualOverride: boolean;
  }[];
}

export interface BitboxBlockResponse {
  data: BitboxBlock[];
  next_page?: string;
  previous_page?: string;
  count_items: number;
}

// Bitbox Report Types
export interface VerificatorUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
}

export interface BitboxReportRequest {
  from_date: string; // ISO 8601 DateTime
  to_date: string; // ISO 8601 DateTime
  verificator_user_ids: string[]; // Array of UUID
}

export interface BitboxReportData {
  block_updated_at: string;
  block_status: string;
  cam1_barcode: string;
  cam1_manual_override: boolean;
  cam1_timestamp: string;
  cam2_barcode: string;
  cam2_manual_override: boolean;
  cam2_timestamp: string;
  cam3_barcode: string;
  cam3_manual_override: boolean;
  cam3_timestamp: string;
  cam4_barcode: string;
  cam4_manual_override: boolean;
  cam4_timestamp: string;
  person_in_charge: string;
}

export interface BitboxReportResponse {
  data: BitboxReportData[];
}

// Common API response wrapper - update to match actual API
export interface ApiResponse<T = any> {
  success?: boolean;
  data?: T;
  message?: string;
  error?: string;
  errors?: Record<string, string[]>;
  // Add actual API error response format
  error_message?: string;
  code?: string;
  validation_error?: any;
}

// Request queue types
export interface QueuedRequest {
  id: string;
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  data?: any;
  headers?: Record<string, string>;
  timestamp: number;
  retryCount: number;
  priority: number;
}

// API configuration
export interface ApiConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
  enableQueue: boolean;
  enableCache: boolean;
  cacheTimeout: number;
}

// Error types
export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(status: number, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

export class NetworkError extends Error {
  constructor(message: string = "Network error occurred") {
    super(message);
    this.name = "NetworkError";
  }
}

export class AuthenticationError extends Error {
  constructor(message: string = "Authentication failed") {
    super(message);
    this.name = "AuthenticationError";
  }
}
